import CardTypeFactory from './cardItem/factory'
import { QuestionFieldNames } from 'src/model/card'
import { AnswerItemType } from 'src/model/card'
import {
  Page,
  Document,
  View,
  Image,
  Svg,
  Path,
  G,
  StyleSheet,
  Font,
  Text,
} from '@react-pdf/renderer'
import { PdfStyleSheet, BackgroundColorRgb, ButtonColorRgb } from './pdf-style'
import URL from 'src/api/url'
import { Entity } from 'draft-js'
import Html from 'react-pdf-html'
import { convertJpgToPng } from './utils'
import { useEffect, useState } from 'react'

// Utility function to convert image URL to base64
const convertImageToBase64 = async imageUrl => {
  try {
    const response = await fetch(imageUrl)
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('Error converting image to base64:', error)
    return null
  }
}

// Function to convert all image sources in HTML to base64
const convertHtmlImagesToBase64 = async html => {
  if (!html) return html

  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Find all img elements
  const imgElements = tempDiv.querySelectorAll('img')

  // Convert all image sources to base64
  const conversionPromises = Array.from(imgElements).map(async img => {
    const imgSrc = img.getAttribute('src')
    if (imgSrc && !imgSrc.startsWith('data:')) {
      // For absolute URLs
      const absoluteSrc = imgSrc.startsWith('http')
        ? imgSrc
        : imgSrc.startsWith('/')
        ? `${window.location.origin}${imgSrc}`
        : `${window.location.origin}/${imgSrc}`

      const base64Data = await convertImageToBase64(absoluteSrc)
      if (base64Data) {
        img.setAttribute('src', base64Data)
      }
    }
  })

  // Wait for all conversions to complete
  await Promise.all(conversionPromises)

  // Return the HTML with base64 images
  return tempDiv.innerHTML
}

const MyDoc = props => {
  const {
    cards,
    themeSettings,
    entity,
    coverText,
    landingPageHeader,
    landingPageMain,
  } = props

  const [processedMainHtml, setProcessedMainHtml] = useState('')
  const [processedCards, setProcessedCards] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const backgroundImageUrl = themeSettings.bodyBackgroundImage
    ? themeSettings.bodyBackgroundImage.replace('svg', 'jpg')
    : ''

  const backgroundColorRgb = BackgroundColorRgb(themeSettings)
  const buttonColorRgb = ButtonColorRgb(themeSettings)

  const styles = PdfStyleSheet(themeSettings)

  useEffect(() => {
    const processAllHtml = async () => {
      setIsLoading(true)

      // Process main landing page HTML
      let mainHtml = ''
      if (landingPageMain && landingPageMain.content.html) {
        mainHtml = await convertHtmlImagesToBase64(landingPageMain.content.html)
        setProcessedMainHtml(mainHtml)
      }

      // Process HTML in all cards
      if (cards && cards.length > 0) {
        const processedCardsArray = await Promise.all(
          cards.map(async card => {
            const cardCopy = { ...card }

            // Check if the card has HTML content to process
            if (cardCopy.content && cardCopy.content.html) {
              cardCopy.content.html = await convertHtmlImagesToBase64(
                cardCopy.content.html,
              )
            }

            // Check other possible HTML fields based on your data structure
            if (cardCopy.question && cardCopy.question.html) {
              cardCopy.question.html = await convertHtmlImagesToBase64(
                cardCopy.question.html,
              )
            }

            if (cardCopy.answer && cardCopy.answer.html) {
              cardCopy.answer.html = await convertHtmlImagesToBase64(
                cardCopy.answer.html,
              )
            }

            return cardCopy
          }),
        )

        setProcessedCards(processedCardsArray)
      } else {
        setProcessedCards(cards || [])
      }

      setIsLoading(false)
    }

    processAllHtml()
  }, [landingPageMain, cards])

  if (!entity || isLoading) {
    return null
  }

  return (
    <Document>
      <Page size="A4" style={[styles.page]} wrap={true}>
        <View style={styles.view}>
          <Text style={styles.courseTitle}>{entity.name}</Text>
          {landingPageHeader && (
            <Image
              style={styles.coverImage}
              src={landingPageHeader && landingPageHeader.content.image}
              cache={false}
            />
          )}
          <Html stylesheet={styles} style={styles.text}>
            {processedMainHtml}
          </Html>
        </View>
      </Page>

      {processedCards
        .filter(
          x =>
            x[QuestionFieldNames.QUESTION_TYPE] == AnswerItemType.INFORM ||
            x[QuestionFieldNames.QUESTION_TYPE] == AnswerItemType.AI ||
            x[QuestionFieldNames.QUESTION_TYPE] ==
              AnswerItemType.TESTINGSINGLECHOICE ||
            x[QuestionFieldNames.QUESTION_TYPE] ==
              AnswerItemType.TESTINGMULTIPLECHOICE ||
            x[QuestionFieldNames.QUESTION_TYPE] ==
              AnswerItemType.SURVEYSINGLECHOICE ||
            x[QuestionFieldNames.QUESTION_TYPE] ==
              AnswerItemType.SURVEYMULTIPLECHOICE ||
            x[QuestionFieldNames.QUESTION_TYPE] == AnswerItemType.SURVEYSTARS ||
            x[QuestionFieldNames.QUESTION_TYPE] ==
              AnswerItemType.SURVEYNUMERICENTRY ||
            x[QuestionFieldNames.QUESTION_TYPE] == AnswerItemType.SURVEYINPUT ||
            x[QuestionFieldNames.QUESTION_TYPE] ==
              AnswerItemType.SURVEYFREEFORM,
        )
        .map(
          (card, index) =>
            card !== undefined && (
              <Page size="A4" style={[styles.page]} wrap={true} key={index}>
                <View style={styles.view}>
                  {CardTypeFactory({
                    type: card[QuestionFieldNames.QUESTION_TYPE],
                    card: card,
                    page: index,
                    stylesheet: styles,
                    style: styles.text,
                    buttonColorRgb: buttonColorRgb,
                    backgroundColorRgb: backgroundColorRgb,
                    editor: undefined,
                  })}
                </View>
              </Page>
            ),
        )}
    </Document>
  )
}

export default MyDoc
